import { EventCodes } from '../model/event-codes';
import type { IRootScopeService, IAngularEvent, IScope } from 'angular';

export type EventHandler = (event: IAngularEvent, ...args: any[]) => any;
export type DestroyHandler = () => void;

/**
 * @ngdoc service
 * @name EventService
 * @module mx.app
 * @requires <a href="https://docs.angularjs.org/api/ng/service/$rootScope" target="_blank">$rootScope</a>
 * @description
 * Service provides methods to dispatch events and to subscribe to them.
 */
export class EventService {
	static $inject = ['$rootScope'];
	constructor(private readonly $rootScope: IRootScopeService) {}
	/**
	 * @ngdoc method
	 * @name EventService#subscribeTo
	 *
	 * @description
	 * Subscribes scope to event
	 *
	 * @param {EventCodes} eventCode - One of the {@ref mx.shell.EventCodes event codes}
	 * @param {function} handler - Event handler
	 * @param {object} scope - A scope that subscribes to event. On scope destruction unsubscribe function will be called
	 * @param {function?} destroyHandler - Event handler that is called when <b>scope is destroyed.<br>
	 * Should be used carefully as it is called on scope destruction not on event unsubscribed</b>
	 *
	 * @returns {function} Event unsubscribe function
	 */
	subscribeTo(eventCode: EventCodes, handler: EventHandler, scope: IScope, destroyHandler?: DestroyHandler): () => void {
		if (!eventCode) {
			throw new Error('`mx.shell.EventService.subscribeTo`: event code is not provided');
		}
		if (!handler || typeof handler !== 'function') {
			throw new Error('`mx.shell.EventService.subscribeTo`: event handler is not provided');
		}
		if (!scope || !scope.$on || typeof scope.$on !== 'function') {
			throw new Error(
				'`mx.shell.EventService.subscribeTo`: <b>scope</b> object is not provided or invalid. To subscribe to rootScope without local scope dependency use `subscribeGlobal` method.'
			);
		}

		const off = this.$rootScope.$on(eventCode, handler);

		scope.$on('$destroy', function () {
			if (destroyHandler && typeof destroyHandler === 'function') {
				destroyHandler();
			}
			off();
		});

		return off;
	}

	/**
	 * @ngdoc method
	 * @name EventService#subscribeGlobal
	 *
	 * @description
	 * Subscribes to an event without local scope dependency.
	 *
	 * @param {EventCodes} eventCode - One of the {@ref mx.shell.EventCodes event codes}
	 * @param {function} handler - Event handler
	 *
	 * @returns {function} Event unsibscribe function
	 */
	subscribeGlobal(eventCode: EventCodes, handler: EventHandler): () => void {
		if (!eventCode) {
			throw new Error('`mx.shell.EventService.subscribeGlobal`: event code is not provided');
		}
		if (!handler || typeof handler !== 'function') {
			throw new Error('`mx.shell.EventService.subscribeGlobal`: event handler is not provided');
		}

		return this.$rootScope.$on(eventCode, handler);
	}

	/**
	 * @ngdoc method
	 * @name EventService#subscribeOnce
	 *
	 * @description
	 * Subscribes grobally to event and after handler executing will be unsubscribed automatically.
	 *
	 * @param {EventCodes} eventCode - One of the {@ref mx.shell.EventCodes event codes}
	 * @param {function} handler - Event handler
	 *
	 * @returns {function} Event unsubscribe function
	 */
	subscribeOnce(eventCode: EventCodes, handler: EventHandler): () => void {
		const off = this.$rootScope.$on(eventCode, function (...params) {
			handler(...params);
			// handler.apply(this, arguments);
			off();
		});

		return off;
	}

	/**
	 * @ngdoc method
	 * @name EventService#dispatchEvent
	 *
	 * @description
	 * Dispatches an event with a specified code and parameters
	 *
	 * @param {EventCodes} code - code of then event to be dispatched
	 * @param {object | array} parameters - Dispatched Event parameters
	 */
	dispatchEvent(code: EventCodes, parameters?: unknown): void {
		if (!code) {
			throw new Error('`mx.shell.EventService.dispatchEvent` event code is not provided');
		}
		this.$rootScope.$emit(code, parameters);
	}
}
